import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import LogUncheckRiskModal from '@/views/dashboard/_components/virus/modals/uncheck-risk-modal.vue'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { getStatusColor, getStatusTitle as getStatusTitleI18n } from '@/services/parse-status.service'
import { formatTimeStamp } from '@/utils/time'
import Copy from '@/views/_components/copy/copy.vue'
import EllipsisText from '@/views/dashboard/_components/ellipsis-text/ellipsis-text.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { FormType, ObjectFormItem, DateType, FormSize } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
import dayjs from 'dayjs'
import SafetyTags from '@/components/safety-tags/safety-tags.vue'
import { useCmTableListRefresh, CmTableListRef } from '@/hooks/use-cm-table-list-refresh'
import { VirusViewService, VirusItem } from './virus-view.service'
import { http } from '@/common/basis/root-injector'
import type { TableActOpt, TableOpt } from '@/components/cmTable/types'
import { formatMapToOptions } from '@/utils/tool'
import { onMounted } from 'vue'

type VirusFilePayload = { fileMd5: string; filePath: string; fileSize: string }

type VirusViewTotalPayload = { total: number; tabKey: string }


export default /*#__PURE__*/_defineComponent({
  __name: 'virus-view',
  props: {
    device_id: {}
  },
  emits: ["changeTotal"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()

const service = new VirusViewService(http)

const tableRef = ref<CmTableListRef | null>(null)
const { refreshList } = useCmTableListRefresh(tableRef)

const pageOption = {
  style: 'padding-bottom: 16px',
  pageNumName: 'page',
  pageSizeName: 'limit',
}

const props = __props

const defaultDateRange = [
  dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD'),
] as [string, string]
const [initialStartTime, initialEndTime] = useParseTime(defaultDateRange)

const queryParams = reactive<Record<string, unknown>>({
  cascade_team_id: undefined,
  handle_status: undefined as number | undefined,
  risk_type: undefined as number | undefined,
  risk_item: -1,
  scan_type: undefined as number | undefined,
  start_time: initialStartTime,
  end_time: initialEndTime,
  key_word: '',
  page: 1,
  limit: 20,
  only_untreated: false,
  sort: '',
  asc: false,
  dateRange: defaultDateRange,
  device_id: props.device_id||undefined
})

const RISK_ITEM_DICT = computed(() => {
  void locale.value
  return Array.from({ length: 23 }, (_, i) => t('responseHandle.riskItem' + i))
})

const scanTypeLabelMap = computed<Record<number, string>>(() => ({
  1: t('safeProtectionModule.lightningScan'),
  2: t('safeProtectionModule.fullDiskScan'),
  3: t('safeProtectionModule.scanTypeDefense'),
  4: t('safeProtectionModule.scanTypeCustom'),
}))

const riskTypeLabelMap = computed<Record<number, string>>(() => ({
  1: t('safeProtectionModule.riskCatWebSettings'),
  2: t('safeProtectionModule.riskCatSystemGeneral'),
  3: t('safeProtectionModule.riskCatSystemSecurity'),
  4: t('safeProtectionModule.riskCatBrowser'),
  5: t('safeProtectionModule.riskCatMobile'),
  6: t('safeProtectionModule.riskCatExtDir'),
  10000: t('safeProtectionModule.riskCatCustomDir'),
  20000: t('safeProtectionModule.fileSecurity'),
  1000000: t('safeProtectionModule.activeDefense'),
}))

const processResultLabelMap = computed<Record<number, string>>(() => ({
  1: t('safeProtectionModule.processUntreated'),
  2: t('safeProtectionModule.processProcessed'),
  3: t('safeProtectionModule.processUntreated'),
  4: t('safeProtectionModule.processTrusted'),
}))

/** 筛选用：含「全部」，不含与「未处理」重复的 3 */
const processResultSearchLabelMap = computed<Record<number, string>>(() => ({
  0: t('common.timeRangeAll'),
  1: t('safeProtectionModule.processUntreated'),
  2: t('safeProtectionModule.processProcessed'),
  4: t('safeProtectionModule.processTrusted'),
}))

const riskItemSearchOptions = computed(() => {
  void locale.value
  const opts = [{ label: t('common.timeRangeAll'), value: -1 }]
  for (let i = 0; i <= 22; i++) {
    opts.push({ label: t(`responseHandle.riskItem${i}`), value: i })
  }
  return opts
})

const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
  void locale.value
  return {
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('noticeManage.filterDateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      outSearchFiledFlag: true,
      events: {
        change: (val: string[] | undefined) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start
            queryParams.end_time = end
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('common.startDate'),
        'end-placeholder': t('common.endDate'),
        clearable: false,
      },
    },
    handle_status: {
      colOpt: { span: 12 },
      label: t('noticeManage.processResult'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: formatMapToOptions(processResultSearchLabelMap.value, true),
    },
    risk_type: {
      colOpt: { span: 12 },
      label: t('noticeManage.riskType'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: formatMapToOptions(riskTypeLabelMap.value, true),
    },
    risk_item: {
      colOpt: { span: 12 },
      label: t('noticeManage.riskItem'),
      type: FormType.Select,
      selectOpt: { clearable: false },
      dictData: riskItemSearchOptions.value,
    },
    scan_type: {
      colOpt: { span: 12 },
      label: t('noticeManage.scanType'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: formatMapToOptions(scanTypeLabelMap.value, true),
    },
  }
})

function getStatusTitle (score: number | string) {
  void locale.value
  return getStatusTitleI18n(score, t)
}

const VIRUS_INFO_K_TO_FIELD: Record<string, keyof VirusFilePayload> = {
  文件MD5: 'fileMd5',
  文件路径: 'filePath',
  文件大小: 'fileSize',
}

function emptyVirusFilePayload (): VirusFilePayload {
  return { fileMd5: '', filePath: '', fileSize: '' }
}

function virusPayloadFromList (list: Array<{ k: string; v: string }>): VirusFilePayload {
  return list.reduce((p: VirusFilePayload, c) => {
    const field = VIRUS_INFO_K_TO_FIELD[c.k]
    if (field) {
      p[field] = c.v
    }
    return p
  }, emptyVirusFilePayload())
}

function parseVirusPayloadList (row: VirusItem): Array<{ k: string; v: string }> {
  try {
    const vi = JSON.parse(row.virus_info || '{}')
    const p = vi.payload
    if (p == null) {
      return []
    }
    const raw = typeof p === 'string' ? JSON.parse(p) : p
    return Array.isArray(raw) ? raw : []
  } catch {
    return []
  }
}

function normalizeVirusRow (row: VirusItem, index: number) {
  const filePayload = virusPayloadFromList(parseVirusPayloadList(row))
  let description = ''
  let engine = ''
  try {
    const vi = JSON.parse(row.virus_info || '{}')
    description = vi?.description ?? ''
    engine = vi?.engine ?? ''
  } catch {
    /* ignore */
  }
  const r = row as VirusItem & {
    tags?: unknown[]
    kscore?: number
    handle_status?: number
    login_status?: number
  }
  const loginStatus =
    r.login_status != null ? r.login_status : (r.device_status != null ? Number(r.device_status) : undefined)
  return {
    ...row,
    risk_desc_display: `【${row.risk_name}】${description}`,
    file_md5_display: filePayload.fileMd5,
    file_path_display: filePayload.filePath,
    file_size_display: filePayload.fileSize || '--',
    engine_display: engine || '--',
    login_status: loginStatus,
    _row_uid: `${row.scan_time ?? ''}-${index}-${row.computer_name ?? ''}`,
  }
}
/** 抽屉内等场景用于同步 tab 角标总数 */
const emit = __emit
async function virusListApi (params: Record<string, unknown>) {
  const body = { ...params } as Record<string, unknown>
  delete body.dateRange
  const ri = body.risk_item
  if (ri === undefined || ri === '' || ri === null) {
    body.risk_item = -1
  }
  const res = await service.fetchVirusList(body as any)
  // emit方法 把total传过去
  emit('changeTotal', { total: Number(res.total ?? 0), tabKey: 'removerLogTab' })
  return {
    total: res.total,
    list: (res.list || []).map((row, index) => normalizeVirusRow(row as VirusItem, index)),
  }
}

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('noticeManage.operation'), width: 100, fixed: 'right', align: 'left' },
    actionButtons: () => [
      {
        name: t('outboundControlLog.dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: 'background: var(--cm-color-card-bg);padding: 0 ',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    height: '100%',
    path: 'list',
    totalPath: 'total',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    showSearch: false,
    rowKey: '_row_uid',
    selection: false,
    showTip: false,
    usePagination: false,
    columns: [
      {
        prop: 'risk_type',
        label: t('noticeManage.riskType'),minWidth: 250,
        formatter: (row) => riskTypeLabelMap.value[Number(row.risk_type)] ?? '--',
      },
      {
        prop: 'risk_item',
        label: t('noticeManage.riskItem'),
        minWidth: 200,
        formatter: (row) => RISK_ITEM_DICT.value[row.risk_item] ?? '--',
      },
      {
        prop: 'tags',
        label: t('ransomwareIncident.threatType'),
        minWidth: 180,
      },
      {
        prop: 'kscore',
        label: t('noticeManage.threatStatus'),
        minWidth: 150,
        hidden: () => HIDE_FUNC.threatIntel,
      },
      {
        prop: 'risk_desc_display',
        label: t('noticeManage.riskDesc'),
        minWidth: 200,
        showOverflow: true,
      },
      {
        prop: 'handle_status',
        label: t('noticeManage.processResult'),
        minWidth: 100,
        formatter: (row) =>
          row.handle_status != null ? processResultLabelMap.value[Number(row.handle_status)] ?? '--' : '--',
      },
      {
        prop: 'scan_time',
        label: t('noticeManage.discoverTime'),
        minWidth: 140,
        formatter: (_row, val) => formatTimeStamp(val, 'YYYY-MM-DD HH:mm'),
      },
      {
        prop: 'scan_type',
        label: t('noticeManage.scanType'),minWidth: 240,
        formatter: (row) => scanTypeLabelMap.value[Number(row.scan_type)] ?? '--',
      },
      {
        prop: 'file_md5_display',
        label: t('noticeManage.fileMd5'),
        minWidth: 280,
        type: 'MD5',
        fieldKey: 'file_md5_display',
      },
      {
        prop: 'computer_name',
        label: t('listColumns.computerName'),
        minWidth: 200,
        type: 'COMPUTER',
      },
      {
        prop: 'user_mark',
        label: t('listColumns.userMark'),
        minWidth: 150,
      },
      {
        prop: 'group_name',
        label: t('listColumns.groupName'),
        minWidth: 150,
      },
      {
        prop: 'client_ip',
        label: t('noticeManage.ipAddress'),
        minWidth: 155,
        type: 'IP',
      },
      {
        prop: 'engine_display',
        label: t('noticeManage.captureEngine'),
        minWidth: 150,
      },
      {
        prop: 'file_path_display',
        label: t('noticeManage.filePath'),
        minWidth: 250,
      },
      {
        prop: 'file_size_display',
        label: t('listColumns.fileSize'),
        minWidth: 200,
        showOverflow: true,
      },
    ],
  }
})

function handleTableAction ({ type, rowItem }: { type: string; rowItem?: VirusItem }) {
  if (type === 'rawData' && rowItem) {
    showRawData(rowItem)
  }
}

async function handleSearch (value: string) {
  queryParams.key_word = value
  await refreshList()
}

async function handleQuery (form: any) {
  Object.assign(queryParams, form)
  if (form.dateRange) {
    const [start, end] = useParseTime(form.dateRange)
    queryParams.start_time = start
    queryParams.end_time = end
  }
  await refreshList()
}

async function handleReset () {
  queryParams.key_word = ''
  queryParams.handle_status = undefined
  queryParams.risk_type = undefined
  queryParams.risk_item = -1
  queryParams.scan_type = undefined
  queryParams.dateRange = [...defaultDateRange]
  queryParams.start_time = initialStartTime
  queryParams.end_time = initialEndTime
  await refreshList()
}

function download (type: 0 | 1) {
  const body = { ...queryParams } as Record<string, unknown>
  delete body.dateRange
  service.download(type, body as any, t('noticeManage.exportFileVirusLogVirusView'))
}

const UNCHECK_RISK_MODAL = ref(false)
const detailModalContent = ref<VirusItem | null>(null)
const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})

function closeVirusScanModal () {
  UNCHECK_RISK_MODAL.value = false
}

function showRawData (row: VirusItem) {
  currentRow.value = row as any
  isShowRawData.value = true
}

onMounted(() => {
  void refreshList()
})

__expose({
  refresh: refreshList,
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      api: virusListApi,
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      "page-option": pageOption,
      "first-loading": false,
      onHandleAction: handleTableAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"0","background-color":"transparent"},
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          "show-search": true,
          class: "cm-ui-pt16",
          "search-placeholder": _ctx.$t('noticeManage.searchPlaceholderRisk'),
          "popover-threshold": 2,
          "force-use-popover": true,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(ExportDropdown, {
              "button-text-i18n-key": "noticeManage.exportRecord",
              onExport: download
            })
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "search-placeholder"])
      ]),
      tags: _withCtx(({ row }) => [
        (row?.tags?.length)
          ? (_openBlock(), _createBlock(SafetyTags, {
              key: 0,
              data: row.tags,
              max: 2
            }, null, 8, ["data"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode("--")
            ], 64))
      ]),
      kscore: _withCtx(({ row }) => [
        _createVNode(_component_el_tag, {
          size: "small",
          style: _normalizeStyle([{"color":"#fff","border":"none"}, `background: ${_unref(getStatusColor)(row.kscore)}`]),
          class: "cm-score-tag"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(getStatusTitle(row.kscore)), 1)
          ]),
          _: 2
        }, 1032, ["style"])
      ]),
      file_path_display: _withCtx(({ row }) => [
        _createVNode(Copy, { reverse: "" }, {
          default: _withCtx(() => [
            _createVNode(EllipsisText, {
              disabled: "",
              content: row.file_path_display || ''
            }, null, 8, ["content"])
          ]),
          _: 2
        }, 1024)
      ]),
      _: 1
    }, 8, ["modelValue", "table-opt", "table-act-opt"]),
    _createVNode(LogUncheckRiskModal, {
      show: UNCHECK_RISK_MODAL.value,
      content: detailModalContent.value,
      onCloseVirusScanModal: closeVirusScanModal
    }, null, 8, ["show", "content"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})